import { createI18n } from "vue-i18n";
import { createDeferredI18n } from "#/shared/composables/useI18n";
import { DEFAULT_LOCALE } from "#/shared/lib/locale";

// Mirrors `frontend.components` in config/locales/frontend.en.yml. Shared by the display
// primitives, so one bundle fetch covers the page however many of them it renders.
//
// The bundle fetch resolves even when the request fails, and an unresolved key renders as the key
// itself, so these seed the instance: a failed fetch degrades to English rather than announcing
// `chip_remove` to a screen reader. The fetched bundle overwrites all of it, including a client's
// own overrides.
const FALLBACK_MESSAGES = Object.freeze({
  alert_dismiss: "Dismiss",
  selection_clear: "Clear selection",
  selection_removed: "{label} removed",
  selection_cleared: "Selection cleared",
  chip_remove: "Remove {label}",
  modal_close: "Close",
  slider_carousel: "carousel",
  slider_slide: "slide",
  slider_default_region: "Slides",
  slider_previous: "Previous slide",
  slider_next: "Next slide",
  slider_go_to: "Go to slide {slide}",
  slider_status: "Slide {slide} of {count}",
  rating_info: "Learn more",
  rating_stars_label: "Rating of {score} out of {max}",
  rating_not_rated: "No Ratings yet.",
  rating_count_label: "Rating | Ratings",
  target_selector_change: "Change",
  target_selector_choose: "Choose {name}",
  target_selector_custom: "Custom {name}",
  target_selector_preview_loading: "Loading…",
  target_selector_preview_title: "Click to view this link in a new tab",
  target_selector_preview_unavailable: "Could not load this target",
  target_selector_remove: "Remove",
  target_selector_use_current: "Use Current",
  table_empty: "No data",
  table_sort_by: "Sort by {label}",
  table_per_page: "Results per Page:",
  table_pagination: "Pagination",
  table_first_page: "First",
  table_previous_page: "Previous",
  table_next_page: "Next",
  table_last_page: "Last",
  table_go_to_page: "Go to page {page}",
  table_page_status: "Page {page} of {pages}",
  table_row_details: "Details for {label}",
  table_row_details_unnamed: "Details",
  filter_bar_scroll_left: "Scroll filters left",
  filter_bar_scroll_right: "Scroll filters right",
  filter_bar_menu_label: "Filters",
  filter_bar_menu_label_count: "Filters ({count})",
  filter_bar_menu_reset: "Reset All",
  filter_bar_menu_reset_title: "Reset all filters",
  filter_bar_menu_clear: "Clear {label}",
  filter_bar_menu_done: "Done",
  finder_query_label: "Query",
  finder_filter_clear_action: "Clear filter",
  finder_filter_toggle_branch: "Show more options",
  menu_close: "Close",
  clear_search: "Clear search",
  text_field_clear: "Clear",
  action_form_confirm_title: "Please Confirm",
  action_form_confirm: "Confirm",
  action_form_cancel: "Cancel",
});

export const { i18n, ready: whenComponentsI18nReady } = createDeferredI18n({ key: "components" });

// The live instance merges each fetched bundle into this object, so it gets its own copy.
i18n.global.setLocaleMessage(i18n.global.locale.value, { ...FALLBACK_MESSAGES });

/**
 * Translates a shared component string. Keys are relative to `frontend.components`, which the i18n
 * endpoint serves as the message root, and resolve per client through that client's own locale
 * files.
 *
 * @param {String} key
 * @param {Object} [values] - Values for the named placeholders of the message.
 * @returns {String}
 */
export function t(key, values) {
  return values ? i18n.global.t(key, values) : i18n.global.t(key);
}

const seeded = createI18n({
  legacy: false,
  locale: DEFAULT_LOCALE,
  missingWarn: false,
  fallbackWarn: false,
  messages: { [DEFAULT_LOCALE]: { ...FALLBACK_MESSAGES } },
});

/**
 * Translates an accessible name. A blank translation (empty or whitespace) resolves to the seeded
 * English instead, so a control whose only name is this string is never left unnamed.
 *
 * @param {String} key
 * @param {Object} [values] - Values for the named placeholders of the message.
 * @returns {String}
 */
export function tAccessibleName(key, values) {
  const message = t(key, values);

  if (message.trim() !== "") {
    return message;
  }

  return values ? seeded.global.t(key, values) : seeded.global.t(key);
}
